<template>
  <div class="company-cards-page">
    <b-sidebar
      id="sidebar-company-form"
      v-model="formVisible"
      bg-variant="white"
      sidebar-class="company-form-sidebar"
      right
      no-header
      backdrop
      shadow
      width="420px"
      @hidden="onFormHidden"
    >
      <div class="company-sidebar">
        <div class="company-sidebar__header">
          <div class="company-sidebar__header-icon">
            <feather-icon icon="BriefcaseIcon" size="20" />
          </div>
          <div class="company-sidebar__header-text">
            <h5 class="mb-0">
              {{ formObj.id > 0 ? $t("Edit Company") : $t("Add Company") }}
            </h5>
            <small class="text-muted">{{
              $t("Code and display name for IUSS campus grouping")
            }}</small>
          </div>
          <button
            type="button"
            class="company-sidebar__close"
            :aria-label="$t('Close')"
            @click="formVisible = false"
          >
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <div class="company-sidebar__body">
          <b-form @submit.prevent="saveCompany">
            <b-form-group
              class="company-field-group"
              :label="$t('Company code') + ' *'"
            >
              <b-form-input
                v-model="formObj.code"
                class="company-field-input company-field-input--code"
                :placeholder="$t('e.g. COC')"
                maxlength="50"
                @input="formObj.code = (formObj.code || '').toUpperCase()"
              />
            </b-form-group>

            <b-form-group
              class="company-field-group"
              :label="$t('Display name') + ' *'"
            >
              <b-form-input
                v-model="formObj.name"
                class="company-field-input"
                :placeholder="$t('Company of Colleges')"
                maxlength="100"
              />
            </b-form-group>

            <div class="company-sidebar__hint">
              <feather-icon icon="InfoIcon" size="16" class="mr-50" />
              {{
                $t(
                  "Link companies on each campus under Branches → edit campus."
                )
              }}
            </div>
          </b-form>
        </div>

        <div class="company-sidebar__footer">
          <b-button
            variant="outline-secondary"
            class="company-sidebar__btn-cancel"
            @click="formVisible = false"
          >
            {{ $t("Cancel") }}
          </b-button>
          <b-button
            v-if="canSaveCompany"
            variant="success"
            class="company-sidebar__btn-save"
            :disabled="saving"
            @click="saveCompany"
          >
            <b-spinner v-if="saving" small class="mr-50" />
            <feather-icon v-else icon="SaveIcon" size="16" class="mr-50" />
            {{ formObj.id > 0 ? $t("Update") : $t("Save") }}
          </b-button>
        </div>
      </div>
    </b-sidebar>

    <b-container fluid class="company-page-container px-2 px-md-3">
      <div class="company-toolbar-card">
        <div class="company-toolbar__actions">
          <button
            v-if="rights.add"
            type="button"
            class="company-add-btn"
            @click="openAdd"
          >
            <span class="company-add-btn__icon">
              <feather-icon icon="PlusIcon" size="18" />
            </span>
            <span>{{ $t("Add Company") }}</span>
          </button>
          <b-button
            variant="light"
            class="company-secondary-btn"
            :to="{ name: 'manage-branches' }"
          >
            <feather-icon icon="HomeIcon" size="16" class="mr-50" />
            {{ $t("Campuses") }}
          </b-button>
        </div>
        <div class="company-search-field">
          <feather-icon
            icon="SearchIcon"
            size="18"
            class="company-search-field__icon"
          />
          <input
            v-model="searchQuery"
            type="search"
            class="company-search-field__input"
            :placeholder="$t('Search companies...')"
          />
        </div>
      </div>

      <div class="company-page-intro">
        <span class="company-page-intro__icon">
          <feather-icon icon="BriefcaseIcon" size="22" />
        </span>
        <div>
          <h4 class="company-page-intro__title mb-0">{{ $t("Companies") }}</h4>
          <p class="company-page-intro__sub mb-0">
            {{ $t("Master list — campuses are linked per company") }}
          </p>
        </div>
        <b-badge v-if="!dataLoading" variant="light-primary" pill class="company-page-intro__count">
          {{ filteredItems.length }}
        </b-badge>
      </div>

      <div v-if="dataLoading" class="company-grid">
        <div
          v-for="n in 6"
          :key="'sk-' + n"
          class="company-grid__col"
        >
          <div class="company-card company-card--skeleton">
            <div class="company-card__top">
              <div class="skeleton-line skeleton-line--code" />
            </div>
            <div class="skeleton-line skeleton-line--title" />
            <div class="skeleton-line skeleton-line--meta" />
          </div>
        </div>
      </div>

      <div v-else-if="!filteredItems.length" class="company-empty">
        <feather-icon icon="BriefcaseIcon" size="40" class="text-muted mb-1" />
        <h5 class="mb-0">{{ $t("No companies yet") }}</h5>
        <small class="text-muted">{{
          $t("Add a company, then assign it on campus profiles.")
        }}</small>
      </div>

      <div v-else class="company-grid">
        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="company-grid__col"
        >
          <article
            class="company-card"
            :class="{ 'company-card--clickable': rights.edit }"
            @click="onCardClick(item)"
          >
            <div class="company-card__top">
              <span class="company-card__code">{{ item.code || "—" }}</span>
              <b-button
                v-if="rights.edit"
                variant="flat-primary"
                size="sm"
                class="company-card__edit btn-icon"
                v-b-tooltip.hover.top
                :title="$t('Edit')"
                @click.stop="openEdit(item)"
              >
                <feather-icon icon="Edit2Icon" size="16" />
              </b-button>
            </div>
            <h3 class="company-card__name">{{ item.name }}</h3>
            <div class="company-card__meta">
              <span class="company-card__meta-icon" aria-hidden="true">
                <feather-icon icon="HomeIcon" size="14" />
              </span>
              <span class="company-card__meta-text">
                <strong>{{ item.campusCount != null ? item.campusCount : 0 }}</strong>
                {{
                  (item.campusCount === 1 ? $t("campus") : $t("campuses"))
                }}
                {{ $t("linked") }}
              </span>
            </div>
          </article>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
import {
  BContainer,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSidebar,
  BSpinner,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import { mapActions } from "vuex";
import { parseCompanyIds } from "@/utils/companyIds";

function emptyCompany() {
  return {
    id: 0,
    code: "",
    name: "",
    type: "",
    isDeleted: false,
    deletedOn: null,
  };
}

export default {
  name: "CompanyCards",
  components: {
    BContainer,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BSidebar,
    BSpinner,
    BBadge,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      rights: {},
      searchQuery: "",
      dataLoading: false,
      saving: false,
      items: [],
      campuses: [],
      formVisible: false,
      formObj: emptyCompany(),
      reportDomain: this.$store.state.report.replace(/_/g, ""),
    };
  },
  computed: {
    filteredItems() {
      const q = (this.searchQuery || "").toLowerCase().trim();
      if (!q) return this.items;
      return this.items.filter((row) => {
        const code = String(row.code || "").toLowerCase();
        const name = String(row.name || "").toLowerCase();
        return code.includes(q) || name.includes(q);
      });
    },
    canSaveCompany() {
      if (Number(this.formObj.id) > 0) return !!this.rights.edit;
      return !!this.rights.add;
    },
  },
  async created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      (el.children || []).forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    this.rights = this.$store.state.rights[this.$route.name] || {};
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    await this.loadAll();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put" }),

    apiDb() {
      return this.$store.state.userData.db;
    },
    apiCid() {
      return this.$store.state.userData.cId;
    },

    async loadCampuses() {
      try {
        const response = await this.get({
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.apiDb() +
            "&cId=" +
            this.apiCid(),
          token: this.$store.state.userData.token,
        });
        this.campuses = Array.isArray(response) ? response : [];
      } catch (e) {
        this.campuses = [];
      }
    },

    countCampusesForCompany(companyId) {
      const id = Number(companyId);
      if (!Number.isFinite(id)) return 0;
      return this.campuses.filter((campus) =>
        parseCompanyIds(campus.companyIDs).includes(id)
      ).length;
    },

    async loadCompanies() {
      const response = await this.get({
        url:
          this.$store.state.domain +
          "Companies?db=" +
          encodeURIComponent(this.apiDb()) +
          "&cID=" +
          this.apiCid(),
        token: this.$store.state.userData.token,
      });
      let rows = [];
      if (Array.isArray(response)) {
        rows = response;
      } else if (response && Array.isArray(response.data)) {
        rows = response.data;
      }
      this.items = rows.map((row) => ({
        ...row,
        campusCount: this.countCampusesForCompany(row.id),
      }));
    },

    async loadAll() {
      this.dataLoading = true;
      try {
        await this.loadCampuses();
        await this.loadCompanies();
      } catch (e) {
        console.error("Companies load failed:", e);
        this.$bvToast.toast(this.$t("Failed to load companies."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.items = [];
      } finally {
        this.dataLoading = false;
      }
    },

    onCardClick(item) {
      if (this.rights.edit) this.openEdit(item);
    },

    openAdd() {
      if (!this.rights.add) return;
      this.formObj = emptyCompany();
      this.formVisible = true;
    },

    async openEdit(item) {
      if (!this.rights.edit) return;
      this.formObj = { ...emptyCompany(), ...item };
      this.formVisible = true;
      if (!item.id) return;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "Companies/GetSelected?id=" +
            item.id +
            "&db=" +
            encodeURIComponent(this.apiDb()),
          token: this.$store.state.userData.token,
        });
        if (res && res !== "NotFound" && typeof res === "object") {
          this.formObj = { ...emptyCompany(), ...res };
        }
      } catch (e) {
        console.warn("GetSelected company failed", e);
      }
    },

    onFormHidden() {
      this.formObj = emptyCompany();
      this.saving = false;
    },

    validateForm() {
      const code = String(this.formObj.code || "").trim();
      const name = String(this.formObj.name || "").trim();
      if (!code || !name) {
        this.$bvToast.toast(this.$t("Code and name are required."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return false;
      }
      return true;
    },

    async saveCompany() {
      if (!this.canSaveCompany) return;
      if (!this.validateForm()) return;
      this.saving = true;
      const body = {
        id: Number(this.formObj.id) || 0,
        code: String(this.formObj.code).trim().toUpperCase(),
        name: String(this.formObj.name).trim(),
        type: String(this.formObj.type || "").trim() || null,
        isDeleted: false,
        deletedOn: null,
      };

      try {
        let ok = false;
        if (body.id > 0) {
          ok = await this.put({
            url:
              this.$store.state.domain +
              "Companies/" +
              body.id +
              "?db=" +
              encodeURIComponent(this.apiDb()) +
              "&userID=" +
              (this.$store.state.userData.userID || 0),
            body,
            message: this.$t("Company updated successfully."),
            token: this.$store.state.userData.token,
            context: this,
            showError: true,
          });
        } else {
          const res = await this.post({
            url:
              this.$store.state.domain +
              "Companies?db=" +
              encodeURIComponent(this.apiDb()),
            body,
            message: this.$t("Company added successfully."),
            token: this.$store.state.userData.token,
            context: this,
            showError: true,
          });
          ok = !!res;
        }
        if (ok) {
          this.formVisible = false;
          await this.loadAll();
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.company-cards-page {
  padding: 0.75rem 0 2rem;
}

.company-page-container {
  max-width: 1400px;
  margin: 0 auto;
}

.company-toolbar-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(115, 103, 240, 0.1);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.06);
}

.company-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  order: 1;
}

.company-search-field {
  position: relative;
  flex: 1 1 280px;
  max-width: 380px;
  margin-left: auto;
  order: 2;

  &__icon {
    position: absolute;
    left: 0.95rem;
    top: 50%;
    transform: translateY(-50%);
    color: #a8aaae;
    pointer-events: none;
  }

  &__input {
    width: 100%;
    min-height: 46px;
    padding: 0.55rem 1rem 0.55rem 2.65rem;
    border: 1px solid #e4e6ef;
    border-radius: 12px;
    background: #f8f8fb;
    font-size: 0.9rem;
    font-weight: 600;
    color: #4b465c;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      outline: none;
      background: #fff;
      border-color: #7367f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.15);
    }
  }
}

.company-page-intro {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1.35rem;
  padding: 0 0.15rem;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 14px;
    color: #fff;
    background: linear-gradient(135deg, #7367f0 0%, #6254e8 100%);
    box-shadow: 0 6px 18px rgba(115, 103, 240, 0.3);
    flex-shrink: 0;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2f2b3d;
    letter-spacing: -0.02em;
  }

  &__sub {
    font-size: 0.88rem;
    color: #6e6b7b;
    margin-top: 0.15rem;
  }

  &__count {
    margin-left: auto;
    font-weight: 700;
    flex-shrink: 0;
  }
}

.company-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: 0;
  border-radius: 12px;
  padding: 0.65rem 1.15rem;
  font-size: 0.92rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #7367f0 0%, #6254e8 100%);
  box-shadow: 0 8px 20px rgba(115, 103, 240, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(115, 103, 240, 0.42);
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.65rem;
    height: 1.65rem;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.18);
  }
}

.company-secondary-btn {
  border-radius: 12px !important;
  font-weight: 600;
  border-color: #ebe9f1 !important;
  color: #5e5873 !important;
  min-height: 44px;
}

.company-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.company-grid__col {
  min-width: 0;
}

.company-empty {
  text-align: center;
  padding: 3rem 1.25rem;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(10px);
  border-radius: 18px;
  border: 1px dashed rgba(115, 103, 240, 0.2);
}

.company-card {
  position: relative;
  padding: 1.05rem 1.1rem 1rem;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(14px) saturate(1.08);
  -webkit-backdrop-filter: blur(14px) saturate(1.08);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 18px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 10px 28px rgba(115, 103, 240, 0.07);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  min-height: 100%;
  transition:
    box-shadow 0.22s ease,
    transform 0.22s ease,
    border-color 0.22s ease;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 1rem;
    right: 1rem;
    height: 3px;
    border-radius: 0 0 4px 4px;
    background: linear-gradient(90deg, #7367f0, #9e95f5);
    opacity: 0.85;
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      border-color: rgba(115, 103, 240, 0.22);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 1) inset,
        0 16px 36px rgba(115, 103, 240, 0.12);
      transform: translateY(-2px);
    }
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding-top: 0.15rem;
  }

  &__code {
    display: inline-flex;
    align-items: center;
    padding: 0.28rem 0.65rem;
    border-radius: 10px;
    font-size: 0.8125rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    line-height: 1.2;
    color: #5e50ee;
    background: rgba(115, 103, 240, 0.1);
    border: 1px solid rgba(115, 103, 240, 0.15);
  }

  &__name {
    font-size: 1.05rem;
    font-weight: 800;
    margin: 0;
    color: #2f2b3d;
    line-height: 1.35;
    word-break: break-word;
    letter-spacing: -0.02em;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-top: auto;
    padding-top: 0.55rem;
    border-top: 1px solid rgba(115, 103, 240, 0.08);
  }

  &__meta-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.65rem;
    height: 1.65rem;
    border-radius: 8px;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.08);
  }

  &__meta-text {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #6e6b7b;

    strong {
      color: #2f2b3d;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
    }
  }

  &__edit {
    width: 2rem !important;
    height: 2rem !important;
    padding: 0 !important;
    border-radius: 10px !important;
    border: none !important;
    background: rgba(115, 103, 240, 0.08) !important;
    color: #7367f0 !important;
    flex-shrink: 0;

    &:hover {
      background: rgba(115, 103, 240, 0.16) !important;
    }
  }

  &--skeleton {
    pointer-events: none;
    min-height: 9.5rem;

    &::before {
      opacity: 0.35;
    }

    .skeleton-line--code {
      width: 4.5rem;
      height: 1.65rem;
      border-radius: 10px;
    }

    .skeleton-line--title {
      width: 78%;
      height: 18px;
      margin: 0;
    }

    .skeleton-line--meta {
      width: 55%;
      height: 12px;
      margin-top: auto;
    }

    .skeleton-line--code,
    .skeleton-line--title,
    .skeleton-line--meta {
      background: linear-gradient(
        90deg,
        #ececee 25%,
        #f6f6f7 50%,
        #ececee 75%
      );
      background-size: 200% 100%;
      animation: shimmer 1.2s infinite;
    }
  }
}

/* Sidebar panel (scoped inner layout) */
.company-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1.25rem 1.25rem 1rem;
    border-bottom: 1px solid #f0edff;
    flex-shrink: 0;
  }

  &__header-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: linear-gradient(135deg, #7367f0 0%, #6254e8 100%);
    flex-shrink: 0;
  }

  &__header-text {
    flex: 1;
    min-width: 0;

    h5 {
      font-weight: 800;
      color: #2d1b69;
      font-size: 1.1rem;
    }

    small {
      display: block;
      margin-top: 0.25rem;
      line-height: 1.35;
    }
  }

  &__close {
    border: none;
    background: #f3f2f7;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5e5873;
    flex-shrink: 0;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #ebe9f1;
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem;
    -webkit-overflow-scrolling: touch;
  }

  &__hint {
    display: flex;
    align-items: flex-start;
    gap: 0.35rem;
    padding: 0.75rem 0.85rem;
    border-radius: 12px;
    background: #f0edff;
    border: 1px solid #e0d8ff;
    font-size: 0.82rem;
    color: #5e5873;
    line-height: 1.4;
  }

  &__footer {
    flex-shrink: 0;
    display: flex;
    gap: 0.65rem;
    padding: 1rem 1.25rem 1.25rem;
    border-top: 1px solid #f0edff;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
  }

  &__btn-cancel {
    flex: 1;
    border-radius: 12px !important;
    font-weight: 600;
    min-height: 44px;
  }

  &__btn-save {
    flex: 1;
    border-radius: 12px !important;
    font-weight: 700;
    min-height: 44px;
    background: linear-gradient(135deg, #10b981 0%, #34d399 100%) !important;
    border: none !important;
    box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35);
  }
}

.company-field-group {
  margin-bottom: 1.1rem;

  ::v-deep label {
    font-weight: 700;
    font-size: 0.82rem;
    color: #4b5563;
  }
}

.company-field-input {
  border-radius: 12px !important;
  border: 1.5px solid #e0d8ff !important;
  min-height: 44px;

  &:focus {
    border-color: #9d7aff !important;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.15) !important;
  }

  &--code {
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
}

.skeleton-block {
  background: linear-gradient(
    90deg,
    #ececee 25%,
    #f6f6f7 50%,
    #ececee 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
}

.skeleton-line {
  height: 12px;
  border-radius: 6px;
  background: #ececee;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 991px) {
  .company-search-field {
    flex: 1 1 100%;
    max-width: none;
    margin-left: 0;
    order: 3;
  }

  .company-toolbar__actions {
    order: 1;
    width: 100%;
  }
}

@media (max-width: 767px) {
  .company-toolbar__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .company-add-btn,
  .company-secondary-btn {
    width: 100%;
  }

  .company-page-intro {
    flex-wrap: wrap;

    &__count {
      margin-left: 0;
      margin-top: 0.25rem;
    }
  }

  .company-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 575px) {
  .company-sidebar__footer {
    flex-direction: column;
  }
}
</style>

<style lang="scss">
/* Bootstrap sidebar shell — full width on small screens */
.company-form-sidebar.b-sidebar {
  width: min(420px, 100vw) !important;
  max-width: 100vw !important;
}

@media (max-width: 575px) {
  .company-form-sidebar.b-sidebar {
    width: 100vw !important;
  }
}
</style>
